// AP-103 FINAL · Context centralizado com as configurações do Portal do Cliente.
// Consome `GET /portal/settings` (autenticado por portal_client token) e propaga
// TODAS as flags configuráveis pelo Master no painel PortalClientSettings.
//
// Estratégia de atualização em tempo real (sem cache stale, sem novo deploy):
//   • Polling a cada 15s enquanto a aba estiver ativa.
//   • Refresh ao voltar para a aba (visibilitychange) e ao focar a janela.
//   • Broadcast via `storage` event (cross-tab) e `portal-settings-updated`
//     custom event (mesma aba) — para que o painel do Master, ao salvar,
//     acione imediatamente o reload no portal aberto em outra aba/janela.
//
// Também aplica dinamicamente:
//   • `--portal-primary` (CSS var) → cor principal do tenant.
//   • Aria-label do brand → nome traduzido.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { portalApi } from "../../context/PortalAuthContext";

// Espelha PORTAL_DEFAULTS do backend — evita render inicial com valores
// que poderiam mostrar botões que o tenant desativou.
const PORTAL_DEFAULTS = {
  portal_payments_enabled: true,
  portal_show_mp_button: true,
  portal_show_amounts: true,
  portal_allow_login: true,
  portal_show_processes: true,
  portal_show_documents: true,
  portal_show_receipts: true,
  portal_show_appointments: true,
  portal_show_notifications: true,
  portal_show_whatsapp: true,
  portal_language: "pt-BR",
  portal_logo_url: null,
  portal_color: "#0057B8",
  portal_welcome_message: "",
};

const POLL_MS = 15000;

const PortalSettingsCtx = createContext({
  settings: PORTAL_DEFAULTS,
  loading: true,
  reload: () => {},
});

// hex → "H S% L%" (para injetar em CSS var HSL do Tailwind).
function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hh = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyBranding(settings) {
  if (typeof document === "undefined") return;
  const root = document.querySelector(".portal-blue-theme") || document.documentElement;
  const triplet = hexToHslTriplet(settings?.portal_color) || hexToHslTriplet("#0057B8");
  if (triplet) {
    root.style.setProperty("--primary", triplet);
    root.style.setProperty("--ring", triplet);
    root.style.setProperty("--portal-primary", triplet);
  }
}

export function PortalSettingsProvider({ children }) {
  const [settings, setSettings] = useState(PORTAL_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal/settings");
      if (!aliveRef.current) return;
      const merged = { ...PORTAL_DEFAULTS, ...(data || {}) };
      setSettings(merged);
      applyBranding(merged);
    } catch {
      // Silencioso — se o portal estiver deslogado, o Provider é montado só
      // quando o cliente está autenticado (pelo PortalLayout).
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load();
    const iv = setInterval(load, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    const onStorage = (e) => { if (e.key === "portal-settings-updated") load(); };
    const onCustom = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener("portal-settings-updated", onCustom);
    return () => {
      aliveRef.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("portal-settings-updated", onCustom);
    };
  }, [load]);

  const value = useMemo(() => ({ settings, loading, reload: load }), [settings, loading, load]);
  return <PortalSettingsCtx.Provider value={value}>{children}</PortalSettingsCtx.Provider>;
}

export function usePortalSettings() {
  return useContext(PortalSettingsCtx);
}

// Utilitário para o painel do Master notificar as janelas do portal
// (mesma origem) que houve uma alteração — abertas em outra aba ou janela.
export function broadcastPortalSettingsUpdated() {
  try {
    localStorage.setItem("portal-settings-updated", String(Date.now()));
    window.dispatchEvent(new Event("portal-settings-updated"));
  } catch {
    // ignore
  }
}
